"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { Pause, Play, SkipForward, ArrowLeft, Sparkles, ChevronRight, BellPlus } from "lucide-react";
import { useRouter } from "next/navigation";
import { API_BASE_URL } from "@/lib/config";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { AuthModal } from "@/components/auth/auth-modal";
import { createChannelLogger } from "@/lib/debug-log";

const replayLog = createChannelLogger("replay_debug");

interface ReplayControlsProps {
  sessionId: string;
  isStreaming: boolean;
  nextUserMessage?: string | null;
  onContinue?: (message: string) => void;
  onComplete?: () => void;
  speed?: number;
  onSpeedChange?: (speed: number) => void;
  isSkipped?: boolean;
  onSkippedChange?: (skipped: boolean) => void;
  replayQuery?: string;
  // ── publish-gate (replay-only) ────────────────────────────────────────
  // When the backend pauses the replay stream right before the
  // upload_alert tool result, it emits a `publish_gate` event with the
  // alert's name + schedule. The parent (`chat.tsx`) reads that event
  // out of the SSE `data` array and passes it down here so we can
  // render an emerald Publish button in the bar — replacing the normal
  // pause/play/skip controls — that, on click, releases the busy-wait
  // server-side and lets the success card stream in.
  publishGate?: {
    alertName: string | null;
    scheduleDescription: string | null;
    toolCallId: string | null;
  } | null;
  // Called BEFORE we hit /replay/control with action=resume. The parent
  // uses it to advance the publish_gate-consumed cursor so the gate
  // vanishes synchronously even if the network round-trip is slow.
  onPublishConfirm?: () => void;
}

const SPEED_OPTIONS = [0.25, 0.5, 1, 2, 4];

export function ReplayControls({
  sessionId,
  isStreaming,
  nextUserMessage,
  onContinue,
  onComplete,
  speed: externalSpeed,
  onSpeedChange: externalSpeedChange,
  isSkipped: externalSkipped,
  onSkippedChange: externalSkippedChange,
  replayQuery,
  publishGate,
  onPublishConfirm,
}: ReplayControlsProps) {
  const router = useRouter();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [internalSpeed, setInternalSpeed] = useState(1);
  const speed = externalSpeed ?? internalSpeed;
  const setSpeed = externalSpeedChange ?? setInternalSpeed;
  const [internalSkipped, setInternalSkipped] = useState(false);
  const isSkipped = externalSkipped ?? internalSkipped;
  const setIsSkipped = externalSkippedChange ?? setInternalSkipped;
  const [speedExpanded, setSpeedExpanded] = useState(false);
  const speedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const sendControl = useCallback(
    async (action: string, speedValue?: number): Promise<boolean> => {
      try {
        const res = await fetch(`${API_BASE_URL}/replay/control`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ session_id: sessionId, action, speed: speedValue }),
        });
        replayLog("sendControl:result", {
          action,
          speedValue,
          ok: res.ok,
          status: res.status,
        });
        return res.ok;
      } catch (e) {
        replayLog("sendControl:error", {
          action,
          speedValue,
          error: e instanceof Error ? e.message : String(e),
        });
        return false;
      }
    },
    [sessionId]
  );

  const togglePause = useCallback(() => {
    const next = !isPaused;
    setIsPaused(next);
    sendControl(next ? "pause" : "resume");
  }, [isPaused, sendControl]);

  const handleSpeed = useCallback(
    (s: number) => {
      setSpeed(s);
      sendControl("set_speed", s);
    },
    [sendControl]
  );

  const handleSkip = useCallback(async () => {
    setIsSkipped(true);
    setIsPaused(false);
    const ok = await sendControl("skip_to_end");
    if (!ok) {
      // Backend hasn't initialized replay state yet — reset so buttons aren't stuck
      setIsSkipped(false);
    }
  }, [sendControl]);

  const handleSkipSubAgent = useCallback(async () => {
    await sendControl("skip_sub_agent");
  }, [sendControl]);

  // Click guard: once we've submitted this Continue, ignore further clicks
  // until `nextUserMessage` clears (parent acknowledges via onContinue →
  // new stream starts → data array resets → useMemo returns null).
  // Without this, impatient double/triple-clicks fire append() multiple
  // times, launching concurrent streams that each reset `data` to 0.
  const continueInFlightRef = useRef(false);
  useEffect(() => {
    // Reset the guard whenever nextUserMessage transitions truthy → null.
    // This means the parent has consumed our click and started the next
    // turn, so future clicks (on a NEW next_user_message) are allowed.
    if (!nextUserMessage) continueInFlightRef.current = false;
  }, [nextUserMessage]);
  const handleContinue = useCallback(() => {
    if (!nextUserMessage || !onContinue) return;
    if (continueInFlightRef.current) {
      replayLog("ctrl:continue-guarded", { nextUserMessage });
      return;
    }
    continueInFlightRef.current = true;
    replayLog("ctrl:continue-click", { nextUserMessage });
    setIsSkipped(false);
    onContinue(nextUserMessage);
  }, [nextUserMessage, onContinue]);

  // Single-flight guard for the publish-gate click. Same shape as the
  // Continue guard above — without it, an impatient double-click fires
  // /replay/control twice and we get a "publish_gate cleared / now
  // gone" race in the parent's `data` cursor.
  const publishInFlightRef = useRef(false);
  useEffect(() => {
    if (!publishGate) publishInFlightRef.current = false;
  }, [publishGate]);
  const handlePublish = useCallback(async () => {
    if (!publishGate) return;
    if (publishInFlightRef.current) {
      replayLog("ctrl:publish-guarded", {});
      return;
    }
    publishInFlightRef.current = true;
    replayLog("ctrl:publish-click", { gate: publishGate });
    // Optimistically clear UI state. Synchronously hides the button so
    // the user gets immediate feedback even before the server resumes.
    onPublishConfirm?.();
    setIsPaused(false);
    // Release the backend busy-wait. Same `resume` action as the
    // existing pause/play toggle — we don't need a new endpoint.
    await sendControl("resume");
  }, [publishGate, onPublishConfirm, sendControl]);

  const isWaiting = !isStreaming && !!nextUserMessage;
  const hasEverStreamed = useRef(false);
  if (isStreaming) hasEverStreamed.current = true;
  const isComplete = !isStreaming && !nextUserMessage && hasEverStreamed.current;
  const isIdle = !isStreaming && !nextUserMessage && !hasEverStreamed.current;
  const hasNotifiedComplete = useRef(false);

  replayLog("ctrl:render", {
    isStreaming,
    nextUserMessage,
    hasEverStreamed: hasEverStreamed.current,
    isComplete,
    isWaiting,
    isIdle,
  });

  // Grace period before committing to "replay is done".
  //
  // ROOT CAUSE: When the backend emits an SSE burst like
  //   0:id 0:id ... 2:[{next_user_message, ...}] d:{finishReason:stop}
  // (typical when speed=4x or skip_to_end fast-forwards), the AI SDK
  // processes events sequentially. React batches commits, but the
  // `status` flip to "ready" and the `data` push of the
  // next_user_message item can commit in SEPARATE renders. There is a
  // ~1-frame window where:
  //   status === "ready"  (isStreaming=false)
  //   data   still short  (nextUserMessage=null, not yet propagated)
  //   hasEverStreamed=true
  // → isComplete transiently becomes `true`. Without the grace window
  // below, onComplete fires, parent sets replayFinished=true, this
  // component unmounts, and the next_user_message arriving 1 frame
  // later has nowhere to render the Continue button.
  //
  // Fix: when isComplete goes true, schedule onComplete for 500ms
  // later. If during that window isComplete flips back to false
  // (because nextUserMessage propagated), cancel the scheduled call.
  // 500ms is generous — React microtask batching resolves in <50ms
  // even on slow devices, but a human reading "Replay Complete"
  // momentarily before Continue appears would be confusing, so we
  // keep the wait short enough that it feels instant.
  useEffect(() => {
    if (!isComplete || hasNotifiedComplete.current || !onComplete) return;
    replayLog("ctrl:complete-pending", {
      isStreaming,
      nextUserMessage,
      hasEverStreamed: hasEverStreamed.current,
    });
    const timeoutId = setTimeout(() => {
      // Re-check isn't possible inside setTimeout (stale closure), so we
      // rely on the cleanup canceling us if isComplete flipped false.
      if (hasNotifiedComplete.current) return;
      hasNotifiedComplete.current = true;
      replayLog("ctrl:fire-onComplete", { after: "500ms-grace" });
      onComplete();
    }, 500);
    return () => {
      clearTimeout(timeoutId);
      replayLog("ctrl:complete-canceled", {
        isStreaming,
        nextUserMessage,
        hasEverStreamed: hasEverStreamed.current,
      });
    };
  }, [isComplete, onComplete, isStreaming, nextUserMessage]);

  return (
    <div
      className="sticky bottom-0 z-40 border-t border-zinc-700/60 bg-zinc-950/95 backdrop-blur-md"
      data-replay-controls="true"
    >
      <div className="relative flex items-center justify-center gap-4 px-5 py-3 mx-auto max-w-3xl">
        {/* Left: Back arrow — absolute so it doesn't shift center */}
        <button
          onClick={() => router.push("/")}
          className="absolute left-5 flex items-center justify-center w-9 h-9 text-zinc-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors shrink-0"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>

        {/* Center: playback controls */}
        <div className="flex items-center gap-3">
          {/* Publish-gate (highest priority — short-circuits the rest).
              Backend has paused the stream right before the upload_alert
              tool result; clicking releases the busy-wait and lets the
              success card stream in. Mirrors the live "Publish?" UX. */}
          {publishGate ? (
            <motion.div
              className="flex flex-col items-center gap-1.5"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            >
              <motion.button
                onClick={handlePublish}
                data-tour="replay-publish"
                className="flex items-center gap-2 px-6 py-2.5 text-sm font-semibold rounded-lg bg-emerald-500 text-black hover:bg-emerald-400 shadow-[0_0_24px_rgba(52,211,153,0.35)] transition-colors"
                animate={{
                  scale: [1, 1.015, 1],
                  x: [0, -1, 1, -0.5, 0.5, 0],
                }}
                transition={{
                  scale: { duration: 1.6, repeat: Infinity, ease: "easeInOut" },
                  x: {
                    duration: 0.5,
                    ease: "easeInOut",
                    delay: 0.6,
                    repeat: Infinity,
                    repeatDelay: 3,
                  },
                }}
              >
                <BellPlus className="w-4 h-4" />
                <span>
                  Publish{publishGate.alertName ? ` "${publishGate.alertName}"` : " alert"}
                </span>
              </motion.button>
              {publishGate.scheduleDescription && (
                <span className="text-[10px] text-zinc-500 tracking-wide">
                  Will run {publishGate.scheduleDescription}
                </span>
              )}
            </motion.div>
          ) : null}
          {/* Streaming controls */}
          {!publishGate && isStreaming && (
            <>
              <button
                onClick={togglePause}
                disabled={isSkipped}
                className="flex items-center justify-center w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 transition-colors disabled:opacity-30"
              >
                {isPaused ? (
                  <Play className="w-4 h-4 text-white ml-0.5" />
                ) : (
                  <Pause className="w-4 h-4 text-white" />
                )}
              </button>

              <div
                className="relative"
                onMouseEnter={() => {
                  if (speedTimeoutRef.current) clearTimeout(speedTimeoutRef.current);
                  setSpeedExpanded(true);
                }}
                onMouseLeave={() => {
                  speedTimeoutRef.current = setTimeout(() => setSpeedExpanded(false), 400);
                }}
              >
                {/* Vertical popover — opens upward */}
                <div
                  className={cn(
                    "absolute bottom-full left-1/2 -translate-x-1/2 mb-2.5 flex flex-col items-center gap-1 p-1.5 rounded-2xl bg-zinc-900/95 backdrop-blur-xl border border-white/[0.08] shadow-[0_8px_40px_rgba(0,0,0,0.5)] transition-all duration-300 ease-out origin-bottom",
                    speedExpanded
                      ? "opacity-100 scale-100 translate-y-0 pointer-events-auto"
                      : "opacity-0 scale-95 translate-y-2 pointer-events-none"
                  )}
                >
                  {[...SPEED_OPTIONS].reverse().map((s) => (
                    <button
                      key={s}
                      onClick={() => { handleSpeed(s); setSpeedExpanded(false); }}
                      disabled={isSkipped}
                      className={cn(
                        "w-10 h-10 rounded-full text-xs font-mono font-semibold transition-all duration-200 disabled:opacity-30",
                        speed === s
                          ? "bg-white text-black scale-105 shadow-[0_0_12px_rgba(255,255,255,0.2)]"
                          : "text-zinc-400 hover:text-white hover:bg-white/10 hover:scale-105"
                      )}
                    >
                      {s}x
                    </button>
                  ))}
                </div>

                {/* Trigger pill */}
                <button
                  disabled={isSkipped}
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 text-xs font-mono font-bold text-white transition-all duration-300 disabled:opacity-30 flex items-center justify-center"
                >
                  {speed}x
                </button>
              </div>

              <button
                onClick={handleSkip}
                disabled={isSkipped}
                className="flex items-center gap-1.5 px-3 py-1.5 ml-1 text-xs font-medium text-zinc-300 hover:text-white bg-white/8 hover:bg-white/15 border border-white/10 rounded-lg transition-colors disabled:opacity-30"
              >
                <SkipForward className="w-3.5 h-3.5" />
                <span>Skip</span>
              </button>
            </>
          )}

          {/* Waiting for Continue */}
          {!publishGate && isWaiting && (
            <motion.button
              onClick={handleContinue}
              data-tour="replay-continue"
              className="flex items-center gap-1.5 px-5 py-2 text-sm font-medium bg-white text-black hover:bg-zinc-200 rounded-lg transition-colors"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{
                opacity: 1,
                scale: 1,
                x: [0, -1.5, 1.5, -1, 1, -0.5, 0],
              }}
              transition={{
                opacity: { duration: 0.3 },
                scale: { duration: 0.3 },
                x: {
                  duration: 0.5,
                  ease: "easeInOut",
                  delay: 0.8,
                  repeat: Infinity,
                  repeatDelay: 4,
                },
              }}
            >
              <span>Continue</span>
              <ChevronRight className="w-4 h-4" />
            </motion.button>
          )}

          {/* Loading skeleton — before stream starts */}
          {!publishGate && isIdle && (
            <div className="flex items-center gap-2">
              <div className="h-2 w-16 bg-white/[0.06] rounded-full animate-pulse" />
              <div className="h-2 w-10 bg-white/[0.04] rounded-full animate-pulse" />
            </div>
          )}

          {/* Replay complete */}
          {!publishGate && isComplete && (
            <span className="text-xs text-zinc-500 font-medium tracking-wide">Replay Complete</span>
          )}
        </div>

        {/* Right: completion CTA for ANONYMOUS viewers only.
            "Try It" → opens signup/auth so they can create an
            account before going deeper.

            Authenticated viewers don't see a button here. Their
            handoff into the rest of the platform happens via the
            replay's "View alert" link → /alerts/[id]?onboarding=1
            → AlertPageBuddyTour → its terminal beat → the
            cross-page platform tour. No completion-state CTA is
            needed in this bar for authed users. */}
        {!user && (
          <button
            onClick={() => {
              if (isMobile) {
                router.push("/signup");
              } else {
                setShowAuthModal(true);
              }
            }}
            className={cn(
              "flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium rounded-lg shrink-0 transition-all duration-300",
              isComplete
                ? "bg-white text-black hover:bg-zinc-200 shadow-[0_0_20px_rgba(255,255,255,0.1)]"
                : "text-zinc-400 hover:text-zinc-200 bg-white/[0.06] hover:bg-white/[0.10] border border-white/[0.08]"
            )}
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>Try It</span>
          </button>
        )}
      </div>

      <AuthModal open={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </div>
  );
}
